// external
import { useCallback, useEffect, useContext, useState } from "react";
import uniqid from "uniqid";
import { toast } from "react-toastify";

// api
import apiRoot from "api/apiRoot";

// state managing/security
import { ClientContext } from "context/ClientContext";
import { FaoBuilderContext } from "context/FaoBuilderContext";

function useTuitionData() {
  const { currentClientData } = useContext(ClientContext);
  const { modelConfig, getExistingConfig, updateModelConfig } =
    useContext(FaoBuilderContext);
  // component id for existing tuition
  const [componentId, setComponentId] = useState(null);

  // tuition data
  const [tuitionBase, setTuitionBase] = useState({});
  const [tuitionAdjusted, setTuitionAdjusted] = useState({});

  // unique id for new tuition
  const uniqueId = uniqid();

  // loading/saving states
  const [loading, setLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setIsSaved(false);
  }, [tuitionAdjusted]);

  const loadExistingData = useCallback(
    async (existingId, baseCOAValue, baseTuitionVal) => {
      try {
        const data = await getExistingConfig({
          configType: "scenario",
          endpoint: "/api/fao-scenarios",
          params: {
            id: existingId,
            schoolKey: currentClientData.schoolKey,
          },
        });

        setTuitionAdjusted({
          tuition: parseFloat(data.adjustedTuition),
          coa: parseFloat(data.adjustedCoa),
        });
      } catch (error) {
        console.error("Error loading scenario data:", error);
        setTuitionAdjusted({
          tuition: baseTuitionVal,
          coa: baseCOAValue,
        });
      }
    },
    [currentClientData.schoolKey, getExistingConfig]
  );

  const loadBaseData = useCallback(async () => {
    try {
      const [tuitionRes, coaRes] = await Promise.all([
        apiRoot.get("/api/fao-scenarios/tuition", {
          params: {
            schoolKey: currentClientData.schoolKey,
            entryTerm: modelConfig.entryTerm,
          },
        }),
        apiRoot.get("/api/fao-scenarios/coa", {
          params: {
            schoolKey: currentClientData.schoolKey,
            entryTerm: modelConfig.entryTerm,
          },
        }),
      ]);

      const baseCOAValue = coaRes.data[0].COA
        ? parseFloat(coaRes.data[0].COA)
        : "No Base Value";
      const baseTuitionVal = tuitionRes.data[0].Academic_Year_Tuition_Fees
        ? parseFloat(tuitionRes.data[0].Academic_Year_Tuition_Fees)
        : "No Base Value";

      setTuitionBase({ tuition: baseTuitionVal, coa: baseCOAValue });
      return { baseCOAValue, baseTuitionVal };
    } catch (error) {
      console.error("Error loading base data:", error);
      setTuitionBase({ tuition: 0, coa: 0 });
      return { baseCOAValue: 0, baseTuitionVal: 0 };
    }
  }, [currentClientData.schoolKey, modelConfig.entryTerm]);
  // Load all data
  useEffect(() => {
    async function handleInitialLoad() {
      try {
        // Load base data first
        const { baseCOAValue, baseTuitionVal } = await loadBaseData();

        // Now load adjusted data after base data is set
        const existingId = modelConfig.configs["scenario"];
        if (existingId) {
          setComponentId(existingId);
          await loadExistingData(existingId, baseCOAValue, baseTuitionVal);
        } else {
          // Use base values when no scenario exists
          setTuitionAdjusted({
            tuition: baseTuitionVal,
            coa: baseCOAValue,
          });
        }
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    }

    handleInitialLoad();
  }, []);

  const handleAdjustedChange = useCallback((key, value) => {
    const numericValue = parseFloat(value.replace(/[^0-9.]/g, ""));
    setTuitionAdjusted((prev) => ({
      ...prev,
      [key]: numericValue || "",
    }));
  }, []);

  const saveTuition = async () => {
    if (
      tuitionAdjusted.tuition === "No Base Value" ||
      !tuitionAdjusted.tuition
    ) {
      toast.error("Please enter a valid tuition value");
      return;
    }
    if (tuitionAdjusted.coa === "No Base Value" || !tuitionAdjusted.coa) {
      toast.error("Please enter a valid COA value");
      return;
    }
    setIsSaving(true);
    try {
      const toastMessages = {
        pending: "Saving Tuition",
        success: "SUCCESS - Tuition saved",
        error: "Tuition not saved",
      };
      const { tuition, coa } = tuitionAdjusted;
      const importData = {
        schoolKey: currentClientData.schoolKey,
        adjustedTuition: tuition,
        adjustedCoa: coa,
      };
      let promise;
      if (componentId) {
        promise = apiRoot.post(
          `/api/fao-scenarios/update-tuition/${componentId}`,
          {
            ...importData,
            id: componentId,
          }
        );
        await toast.promise(promise, toastMessages);
      } else {
        setComponentId(uniqueId);
        promise = apiRoot.post(`/api/fao-scenarios`, {
          ...importData,
          id: uniqueId,
        });
        await toast.promise(promise, toastMessages);
        await updateModelConfig("scenario", uniqueId);
      }
    } catch (error) {
      console.log(error);
    }
    setIsSaving(false);
    setIsSaved(true);
  };

  return {
    tuitionBase,
    tuitionAdjusted,
    loading,
    isSaved,
    isSaving,
    componentId,
    setComponentId,
    handleAdjustedChange,
    saveTuition,
  };
}

export default useTuitionData;
